import React from "react";
import { Selection } from "src/hooks/useMultiselectState";
import {
  SchoolFormKeyRecordFactory,
  type FormIdListResult,
  type SchoolFormId,
  type SchoolFormKeyRecord,
} from "../types";
import {
  BulkActionTargetsContext,
  type BulkTargetsResult,
  type FetchAllIds,
} from "./bulkActionTargetsContext";
import { resolveBulkTargets } from "./bulkTargets";

export function BulkActionTargetsProvider({
  selection,
  formTemplateId,
  fetchAllIds,
  onClearSelection,
  children,
}: {
  selection: Selection<SchoolFormKeyRecord, unknown>;
  formTemplateId: string;
  fetchAllIds: FetchAllIds;
  onClearSelection: () => void;
  children: React.ReactNode;
}) {
  const snapshot = React.useRef<{
    fetchAllIds: FetchAllIds;
    cacheKey: uuid | null;
  } | null>(null);
  const pendingLoad = React.useRef<{
    fetchAllIds: FetchAllIds;
    promise: Promise<FormIdListResult>;
  } | null>(null);

  React.useEffect(() => {
    snapshot.current = null;
    pendingLoad.current = null;
  }, [fetchAllIds, formTemplateId]);

  const fetchSnapshot = React.useCallback((): Promise<FormIdListResult> => {
    const pending = pendingLoad.current;
    if (pending?.fetchAllIds === fetchAllIds) {
      return pending.promise;
    }

    const promise: Promise<FormIdListResult> = fetchAllIds().then(
      (result) => {
        if (pendingLoad.current?.promise === promise) {
          pendingLoad.current = null;
          snapshot.current = { fetchAllIds, cacheKey: result.cacheKey };
        }
        return result;
      },
      (error) => {
        if (pendingLoad.current?.promise === promise) {
          pendingLoad.current = null;
        }
        throw error;
      }
    );

    pendingLoad.current = { fetchAllIds, promise };
    return promise;
  }, [fetchAllIds]);

  React.useEffect(() => {
    if (selection.isInclusive) return;
    fetchSnapshot().catch(() => undefined);
  }, [fetchSnapshot, selection.isInclusive]);

  const loadTargets = React.useCallback(async (): Promise<SchoolFormId[]> => {
    if (selection.isInclusive) {
      snapshot.current = null;
      pendingLoad.current = null;
      return selection.keysIncludedOrExcluded.map((key) => key.toObject());
    }

    const { items } = await fetchSnapshot();
    return items.filter((item) =>
      selection.includesKey(SchoolFormKeyRecordFactory(item))
    );
  }, [fetchSnapshot, selection]);

  const resolveTargets =
    React.useCallback(async (): Promise<BulkTargetsResult> => {
      if (selection.isInclusive) {
        const targets = resolveBulkTargets(selection, formTemplateId);
        return targets
          ? { targets }
          : { targets: null, reason: "no-selection" };
      }

      const loaded =
        snapshot.current?.fetchAllIds === fetchAllIds
          ? snapshot.current
          : await fetchSnapshot();
      const targets = resolveBulkTargets(
        selection,
        formTemplateId,
        loaded.cacheKey
      );
      return targets ? { targets } : { targets: null, reason: "empty-list" };
    }, [fetchAllIds, fetchSnapshot, formTemplateId, selection]);

  const value = React.useMemo(
    () => ({
      loadTargets,
      resolveTargets,
      selectionSize: selection.size,
      clearSelection: onClearSelection,
    }),
    [loadTargets, onClearSelection, resolveTargets, selection.size]
  );

  return (
    <BulkActionTargetsContext.Provider value={value}>
      {children}
    </BulkActionTargetsContext.Provider>
  );
}
